import { computed, effect, inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslationKey } from '@tolgee/ngx';
import { of, take } from 'rxjs';
import { ApiScenarioResponse } from '../../api/core/models';
import { QUERY_PARAMS } from '../../app.constants';
import { EDITOR_ROUTE_PATHS } from '../../app.routes.constant';
import { injectTranslate } from '../../functions/inject-functions/inject-translate';
import { UI_STRINGS } from '../../functions/language/uistrings-tolgee';
import { ToastLife, ToastType } from '../../modules/shared/ui-kit/toast/toast.component.types';
import { MessageService } from '../../services/message.service';
import { ScenariosStore } from './scenarios.store';

const ALLOWED_ROUTES_IN_NON_PRIMARY: string[] = [EDITOR_ROUTE_PATHS.TRANSITIONS.ROOT];
const NOT_ALLOWED_CHILD_ROUTES_IN_TRANSITIONS: string[] = [
  EDITOR_ROUTE_PATHS.TRANSITIONS.CHILDREN.TRANSITION_HIERARCHY,
];
const BOARD_EDITOR_STRINGS = UI_STRINGS.BOARD_EDITOR;
const GLOBAL_STRINGS = UI_STRINGS.GLOBAL;

@Injectable({ providedIn: 'root' })
export class ScenariosFacadeService {
  private readonly _store = inject(ScenariosStore);
  private readonly _router = inject(Router);
  private readonly _messageService = inject(MessageService);
  error = this._store.error;
  isLoading = this._store.isLoading;
  scenarios = this._store.scenarios;
  selectedScenario = this._store.selectedScenario;

  private _pendingNavigationToast: { routeName: string; scenarioName: string } | null = null;
  private readonly ROUTE_NAME_TO_TITLE_MAP: Record<string, TranslationKey> = {
    [EDITOR_ROUTE_PATHS.HOME.ROOT]: BOARD_EDITOR_STRINGS.NAVLINK_HOME,
    [EDITOR_ROUTE_PATHS.INVENTORIES.ROOT]: BOARD_EDITOR_STRINGS.NAVLINK_INVENTORY,
    [EDITOR_ROUTE_PATHS.INTERVENTIONS.ROOT]: GLOBAL_STRINGS.INTERVENTIONS,
    [EDITOR_ROUTE_PATHS.INVESTMENT_PLAN]: BOARD_EDITOR_STRINGS.NAVLINK_INVESTMENT_PLAN,
    [EDITOR_ROUTE_PATHS.DASHBOARD.ROOT]: BOARD_EDITOR_STRINGS.NAVLINK_DASHBOARD,
    [EDITOR_ROUTE_PATHS.TRANSITIONS.CHILDREN.TRANSITION_HIERARCHY]:
      BOARD_EDITOR_STRINGS.NAVLINK_PATHWAYS_TRANSITIONHIERARCHY,
  };
  private readonly SCENARIOS_STRINGS = UI_STRINGS.SCENARIOS;
  private readonly _translate = injectTranslate();

  constructor() {
    effect(() => {
      const activeScenario = this.selectedScenario();
      if (activeScenario && !activeScenario.isPrimary) {
        this._showNotPrimaryScenarioToast(activeScenario.name);

        const isNewPrimary = activeScenario?.isPrimary;
        const currentRoute = this._router.url;

        if (!isNewPrimary && currentRoute) {
          this._handleNavigationForNonPrimaryScenario(currentRoute, activeScenario);
        }
      }
    });
    // Listen for navigation events to show toast after navigation completes
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && this._pendingNavigationToast) {
        // Show toast after navigation completes
        this._showNavigationToast(this._pendingNavigationToast.routeName, this._pendingNavigationToast.scenarioName);
        this._pendingNavigationToast = null;
      }
    });
  }

  copyScenario(name: string, description: string, originScenarioId: string) {
    this._store.createScenario({ name, description, originScenarioId });
  }

  createScenario(name: string, description: string) {
    this._store.createScenario({ name, description });
  }

  deleteScenario(id: string) {
    this._store.deleteScenario({ id });
  }

  listScenarios() {
    if (!this._store.scenarios()) {
      // Load scenarios - the store will handle getting the scenarioId from query params
      this._store.loadScenarios();
    }
    return computed(() => this._store.scenarios());
  }

  updateScenario(id: string, data: { name: string; description: string }) {
    this._store.updateScenario({ id, ...data });
  }

  setScenarioAsPrimary(id: string) {
    this._store.updateScenario({ id, isPrimary: true });
  }

  /**
   * Set the current scenario by ID and handle navigation if needed
   * @param scenarioId The ID of the scenario to set as current
   * @param currentRoute The current route path to check if redirection is needed
   * @returns true if the scenario was found and set, false otherwise
   */
  setCurrentScenarioById(scenarioId: string, currentRoute: string): boolean {
    this._store.setCurrentScenarioById(scenarioId);

    const isNewPrimary = this._store.selectedScenario()?.isPrimary;
    const newScenario = this._store.selectedScenario();

    // If switching from primary to non-primary and we're on a restricted route, handle redirection
    if (isNewPrimary && currentRoute) {
      this._handleNavigationForNonPrimaryScenario(currentRoute, newScenario);
    }

    return true;
  }

  /**
   * Check if a route is allowed in non-primary scenarios
   * @param routePath The route path to check
   */
  isRouteAllowedInNonPrimaryScenario(routePath: string): boolean {
    // Extract the main route segment
    const { mainRoute, childRoute } = this.extractRoute(routePath);
    return (
      ALLOWED_ROUTES_IN_NON_PRIMARY.includes(mainRoute) && !NOT_ALLOWED_CHILD_ROUTES_IN_TRANSITIONS.includes(childRoute)
    );
  }

  /**
   * Extract the main route from a path
   * @param path The full route path
   */
  extractRoute(path: string): { mainRoute: string; childRoute: string } {
    // Remove query parameters if present
    const pathWithoutQuery = path.split('?')[0];

    // URL pattern is typically /app/[orgId]/board/[boardId]/[route]
    const parts = pathWithoutQuery.split('/');
    const mainRoute = parts.length >= 6 ? parts[5] : '';
    const childRoute = parts.length >= 6 ? parts[6] : '';
    return { mainRoute, childRoute };
  }

  /**
   * Handle navigation when switching to a non-primary scenario
   * @param currentRoutePath The current route path
   * @param newScenario The new scenario
   */
  private _handleNavigationForNonPrimaryScenario(
    currentRoutePath: string,
    newScenario: ApiScenarioResponse | undefined
  ): void {
    const { mainRoute, childRoute } = this.extractRoute(currentRoutePath);

    if (
      !ALLOWED_ROUTES_IN_NON_PRIMARY.includes(mainRoute) ||
      NOT_ALLOWED_CHILD_ROUTES_IN_TRANSITIONS.includes(childRoute)
    ) {
      // Get current organization and board from URL
      const urlParts = currentRoutePath.split('/');
      // URL pattern is typically /app/[orgId]/board/[boardId]/[route]
      const orgId = urlParts[2];
      const boardId = urlParts[4];

      // Navigate to transitions route
      const transitionsRoute = `organisation/${orgId}/board/${boardId}/${EDITOR_ROUTE_PATHS.TRANSITIONS.ROOT}`;
      const targetUrl = `/${transitionsRoute}`;

      // Only redirect if we're not already on the transitions route
      if (currentRoutePath.split('?')[0] !== targetUrl) {
        // Queue toast to be shown after navigation completes
        this._pendingNavigationToast = {
          routeName: NOT_ALLOWED_CHILD_ROUTES_IN_TRANSITIONS.includes(childRoute) ? childRoute : mainRoute,
          scenarioName: newScenario?.name ?? '',
        };

        // Redirect to transitions
        // queueMicrotask is used to ensure navigation happens after the current change detection cycle
        queueMicrotask(() => {
          this._router.navigate([transitionsRoute], {
            queryParams: { [QUERY_PARAMS.SCENARIO_ID]: newScenario?.id },
            queryParamsHandling: 'merge',
          });
        });
      }
    }
  }

  /**
   * Show a toast explaining the navigation change
   * @param routeName The name of the route that's not available
   * @param scenarioName The name of the scenario being switched to
   */
  private _showNavigationToast(routeName: string, scenarioName: string): void {
    const routeNameTranslationKey: TranslationKey | undefined = this.ROUTE_NAME_TO_TITLE_MAP[routeName];
    const routeName$ = !routeNameTranslationKey ? of('') : this._translate.toObservable(routeNameTranslationKey);
    routeName$.pipe(take(1)).subscribe((routeNameTranslated) => {
      const title = this.SCENARIOS_STRINGS.NOT_AVAILABLE_TITLE;
      const text = this.SCENARIOS_STRINGS.NOT_AVAILABLE_TEXT;

      this._messageService.openToast({
        title,
        titleParams: [routeNameTranslated],
        text,
        textParams: [routeNameTranslated, scenarioName],
        type: ToastType.INFORMATION,
        toastLife: ToastLife.LONG,
      });
    });
  }

  /**
   * Show a toast explaining the non-primary scenario change
   * @param scenarioName The name of the scenario being switched to
   */
  private _showNotPrimaryScenarioToast(scenarioName: string): void {
    const title = this.SCENARIOS_STRINGS.NOT_PRIMARY_SCENARIO_TITLE;
    const text = this.SCENARIOS_STRINGS.NOT_PRIMARY_SCENARIO_TEXT;

    this._messageService.openToast({
      title,
      text,
      textParams: [scenarioName],
      type: ToastType.INFORMATION,
      toastLife: ToastLife.LONG,
    });
  }
}
